import { useLayoutEffect, useRef, RefObject, useState } from 'react';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { useIsMounted } from '@hooks/useIsMounted';
import ScrollMagic from 'scrollmagic';
import { gsap, Linear } from 'gsap';
import { cloudAssetUrl } from '@utils/cloudAssetUrl.util';
import { useLottieAnimation } from '@hooks/useLottieAnimation';
import { AnimationItem } from 'lottie-web';

export const useGuestListStaticHeroController = () => {
  const heroAnimationWrapperRef = useRef<HTMLDivElement>(null);
  const heroAnimationWrapperEl = heroAnimationWrapperRef.current;
  const lottieContainerRef = useRef<HTMLDivElement>(null);
  const lottieContainerEl = lottieContainerRef.current;
  const groupRef = useRef<HTMLDivElement>(null);
  const organizeRef = useRef<HTMLDivElement>(null);
  const inviteRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const heroLottieRef = useRef<HTMLDivElement>(null);
  const groupLottieRef = useRef<HTMLDivElement>(null);
  const organizeLottieRef = useRef<HTMLDivElement>(null);
  const inviteLottieRef = useRef<HTMLDivElement>(null);
  const trackLottieRef = useRef<HTMLDivElement>(null);
  const groupTopRef = useRef<HTMLDivElement>(null);
  const groupBackRef = useRef<HTMLDivElement>(null);
  const organizeTopRef = useRef<HTMLDivElement>(null);
  const organizeBackRef = useRef<HTMLDivElement>(null);
  const inviteTopRef = useRef<HTMLDivElement>(null);
  const inviteBackRef = useRef<HTMLDivElement>(null);
  const trackTopRef = useRef<HTMLDivElement>(null);
  const trackBackRef = useRef<HTMLDivElement>(null);

  const scrollController = useScrollController();
  const isMounted = useIsMounted();
  const [lottieOffse, setLottieOffse] = useState(0);

  const magneticAvatarClass = 'Hero_Magnetic_Avatar';
  const magneticAvatarLeftClass = 'Hero_Magnetic_Avatar_Left';

  type AnimationConfigWithForcePath = {
    renderer: 'svg';
    loop: boolean;
    autoplay: boolean;
    rendererSettings: {
      progressiveLoad: boolean;
      preserveAspectRatio: 'xMidYMid slice';
    };
  };

  const defaultLottieConfig: AnimationConfigWithForcePath = {
    renderer: 'svg', // Required
    loop: false, // Optional
    autoplay: false, // Optional
    rendererSettings: {
      progressiveLoad: false,
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  const { animationRef: heroAnimationRef, loadAnimation: heroLoadAnimation } = useLottieAnimation(
    {
      ...defaultLottieConfig,
      path: cloudAssetUrl('guest-list/hero-vid/gl_jpg_lottie/guestlist_anim_jpg.json'), // Required
      name: 'guestlist organize',
    },
    { containerRef: heroLottieRef },
    'aggressive'
  );

  const { animationRef: groupAnimationRef, loadAnimation: groupLoadAnimation } = useLottieAnimation(
    {
      ...defaultLottieConfig,
      path: cloudAssetUrl('guest-list/hero-vid/gl_icons_lottie/gl_group_icon.json'), // Required
      name: 'guestlist group',
    },
    { containerRef: groupLottieRef },
    'aggressive'
  );

  const { animationRef: organizeAnimationRef, loadAnimation: organizeLoadAnimation } = useLottieAnimation(
    {
      ...defaultLottieConfig,
      path: cloudAssetUrl('guest-list/hero-vid/gl_icons_lottie/gl_tags_icon.json'), // Required
      name: 'guestlist organize',
    },
    { containerRef: organizeLottieRef },
    'aggressive'
  );

  const { animationRef: inviteAnimationRef, loadAnimation: inviteLoadAnimation } = useLottieAnimation(
    {
      ...defaultLottieConfig,
      path: cloudAssetUrl('guest-list/hero-vid/gl_icons_lottie/gl_invite_icon.json'), // Required
      name: 'guestlist organize',
    },
    { containerRef: inviteLottieRef },
    'aggressive'
  );

  const { animationRef: trackAnimationRef, loadAnimation: trackLoadAnimation } = useLottieAnimation(
    {
      ...defaultLottieConfig,
      path: cloudAssetUrl('guest-list/hero-vid/gl_icons_lottie/gl_track_icon.json'), // Required
      name: 'guestlist organize',
    },
    { containerRef: trackLottieRef },
    'aggressive'
  );

  useLayoutEffect(() => {
    const initSideAnimation = (
      heroTimeline: ReturnType<typeof gsap.timeline>,
      topRef: RefObject<HTMLDivElement>,
      backRef: RefObject<HTMLDivElement>,
      topStart: number,
      backStart: number,
      hideTime: number,
      isLast?: boolean
    ) => {
      heroTimeline.set(topRef.current, { opacity: 1 }, 'start+=' + (topStart - 0.1));
      heroTimeline.set(backRef.current, { opacity: 1 }, 'start+=' + (topStart - 0.1));
      heroTimeline.to(topRef.current, isLast ? 0.65 : 0.2, { y: -1900 }, 'start+=' + topStart);
      heroTimeline.to(backRef.current, isLast ? 0.325 : 0.4, { y: -2200 }, 'start+=' + backStart);
      heroTimeline.set(topRef.current, { opacity: 0 }, 'start+=' + hideTime);
      heroTimeline.set(backRef.current, { opacity: 0 }, 'start+=' + hideTime);
    };
    const initAnimation = (
      heroTimeline: ReturnType<typeof gsap.timeline>,
      ref: RefObject<HTMLDivElement>,
      animationRef: RefObject<AnimationItem | null>,
      startTime: number,
      playTime: number,
      hideTime: number,
      isLast?: boolean
    ) => {
      heroTimeline.call(() => animationRef.current?.goToAndPlay(0), [], 'start+=' + (startTime - 0.1));
      heroTimeline.set(ref.current, { opacity: 1, y: 600 }, 'start+=' + startTime);
      heroTimeline.to(ref.current, 0.1, { y: isLast ? 0 : -600 }, 'start+=' + startTime);
      heroTimeline.call(() => animationRef.current?.goToAndPlay(0), [], 'start+=' + playTime);
      heroTimeline.set(ref.current, { opacity: 0 }, 'start+=' + hideTime);
    };

    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let heroTimeline: ReturnType<typeof gsap.timeline>;

    if (scrollController && isMounted && lottieContainerEl) {
      const initTimeline = () => {
        if (scrollController) {
          heroTimeline = gsap.timeline({ paused: true });
          heroTimeline.add('start');
          heroTimeline.to(
            { frame: 0 },
            1,
            {
              frame: 10,
              onUpdate: function () {
                heroAnimationRef.current?.goToAndStop(Math.round(this.progress() * 950), true);
              },
              ease: Linear.easeNone,
            },
            'start'
          );
          initSideAnimation(heroTimeline, groupTopRef, groupBackRef, 0, 0.124, 0.46, true);
          initAnimation(heroTimeline, groupRef, groupAnimationRef, 0.185, 0.215, 0.27);

          initSideAnimation(heroTimeline, organizeTopRef, organizeBackRef, 0.433, 0.39, 0.65);
          initAnimation(heroTimeline, organizeRef, organizeAnimationRef, 0.47, 0.496, 0.55);

          initSideAnimation(heroTimeline, inviteTopRef, inviteBackRef, 0.617, 0.58, 0.85);
          initAnimation(heroTimeline, inviteRef, inviteAnimationRef, 0.64, 0.67, 0.73);

          initSideAnimation(heroTimeline, trackTopRef, trackBackRef, 0.85, 0.8, 1.5);
          initAnimation(heroTimeline, trackRef, trackAnimationRef, 0.82, 0.92, 1.5, true);
        }
      };

      const initScene = () => {
        scene = new ScrollMagic.Scene({
          triggerElement: lottieContainerEl,
          triggerHook: 'onCenter',
          offset: lottieOffse,
          duration: 7500,
        })
          .setPin(lottieContainerEl)
          .addTo(scrollController)
          .on('enter', () => {
            setLottieOffse(lottieContainerEl.clientHeight / 2);
          })
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            heroTimeline?.progress(event.progress / 1.4);
          });
      };
      initTimeline();
      initScene();
    }

    let sceneAvatar: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let timelineAvatar: ReturnType<typeof gsap.timeline>;
    const moveXYOffset = 25;

    if (scrollController && isMounted && heroAnimationWrapperEl) {
      // Import Button animation
      timelineAvatar = gsap.timeline({ paused: true });
      timelineAvatar.set(`.${magneticAvatarLeftClass}`, { x: 0, y: 0 }, 0);
      timelineAvatar.set(`.${magneticAvatarClass}`, { x: 0, y: 0 }, 0);
      timelineAvatar.to(
        `.${magneticAvatarLeftClass}`,
        {
          x: `-=${moveXYOffset}vw`,
          y: `-=${moveXYOffset}vw`,
        },
        0
      );
      timelineAvatar.to(
        `.${magneticAvatarClass}`,
        {
          x: `+=${moveXYOffset}vw`,
          y: `-=${moveXYOffset}vw`,
        },
        0
      );
      sceneAvatar = new ScrollMagic.Scene({
        triggerElement: heroAnimationWrapperEl,
        triggerHook: 1,
        duration: 500,
        offset: window.innerHeight - heroAnimationWrapperEl.offsetTop + 15,
      })
        .addTo(scrollController)
        .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
          timelineAvatar?.progress(event.progress);
        });
    }

    const clearScene = () => {
      if (heroTimeline) {
        heroTimeline.progress(0);
        heroTimeline.kill();
      }
      if (scene) {
        scene.destroy(true);
      }
      if (timelineAvatar) {
        timelineAvatar.progress(0);
        timelineAvatar.kill();
      }
      if (sceneAvatar) {
        sceneAvatar.destroy(true);
      }
    };
    return clearScene;
  }, [
    scrollController,
    isMounted,
    heroAnimationRef,
    groupAnimationRef,
    organizeAnimationRef,
    inviteAnimationRef,
    trackAnimationRef,
    lottieContainerEl,
    lottieOffse,
    groupTopRef,
    groupBackRef,
    organizeTopRef,
    organizeBackRef,
    inviteTopRef,
    inviteBackRef,
    trackTopRef,
    trackBackRef,
    heroAnimationWrapperEl,
  ]);

  return {
    heroAnimationWrapperRef,
    lottieContainerRef,
    heroLottieRef,
    heroLoadAnimation,
    groupRef,
    groupLottieRef,
    groupLoadAnimation,
    organizeRef,
    organizeLottieRef,
    organizeLoadAnimation,
    inviteRef,
    inviteLottieRef,
    inviteLoadAnimation,
    trackRef,
    trackLottieRef,
    trackLoadAnimation,
    groupTopRef,
    groupBackRef,
    organizeTopRef,
    organizeBackRef,
    inviteTopRef,
    inviteBackRef,
    trackTopRef,
    trackBackRef,
    magneticAvatarClass,
    magneticAvatarLeftClass,
  };
};
